import { ReactNode, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar, { AppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

// project-imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH, MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { clacUiGlobal as ui } from 'utils/uiGlobal';

// assets
import { HamburgerMenu } from 'iconsax-reactjs';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();

  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
  const isCuestionarioWizard = useMemo(() => /^\/cuestionarios\/nuevo(\/plantilla)?$/.test(location.pathname), [location.pathname]);

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader: ReactNode = (
    <Toolbar sx={{ px: { xs: 2, sm: 2.5, md: 5, lg: 5 }, minHeight: ui.layout.topbarHeight }}>
      {!isHorizontal && !isCuestionarioWizard ? (
        <IconButton
          aria-label="open drawer"
          onClick={() => handlerDrawerOpen(!drawerOpen)}
          edge="start"
          color="secondary"
          variant="light"
          size="large"
          sx={{
            color: ui.semantic.text.secondary,
            bgcolor: drawerOpen ? ui.semantic.bg.page : ui.semantic.n[200],
            ml: { xs: 0, lg: -2 },
            p: 1
          }}
        >
          <HamburgerMenu />
        </IconButton>
      ) : null}
      {!isHorizontal && isCuestionarioWizard ? (
        <IconButton
          aria-label="back"
          onClick={() => navigate(location.pathname.includes('/plantilla') ? '/cuestionarios/nuevo' : '/cuestionarios')}
          edge="start"
          variant="text"
          color="secondary"
          sx={{
            width: 28,
            height: 28,
            borderRadius: ui.radius.sm,
            border: `1.5px solid ${ui.semantic.border.subtle}`,
            bgcolor: ui.semantic.bg.surface,
            color: ui.semantic.text.secondary,
            ml: { xs: 0, lg: -2 },
            '&:hover': { bgcolor: ui.semantic.border.subtle }
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 2L4 7l5 5" />
          </svg>
        </IconButton>
      ) : null}
      {headerContent}
    </Toolbar>
  );

  // app-bar params
  const appBar: AppBarProps = {
    position: 'fixed',
    elevation: 0,
    sx: {
      bgcolor: ui.semantic.bg.surface,
      borderBottom: '1px solid',
      borderBottomColor: ui.semantic.border.subtle,
      zIndex: 1200,
      width: isHorizontal
        ? '100%'
        : { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` }
    }
  };

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
}
